/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ImageCard } from '../../services/details/ImageCard';
import { WorkshopBookingFlow } from '../booking/WorkshopBookingFlow';
import { PortableText } from '@portabletext/react';

interface WorkshopDetailsProps {
	workshop: {
		_id: string;
		day: string;
		month: string;
		year: string;
		title: string;
		date: string;
		time: string;
		location: string;
		variant: 'dark' | 'light';
		calendarIconUrl: string;
		price: string;
		limit: number;
		image?: string;
		description?: string;
		content: any;
		benefits?: string[];
	};
}

export default function WorkshopDetails({ workshop }: WorkshopDetailsProps) {
	const navigate = useNavigate();
	const isSoldOut = workshop.limit === 0;
	const [bookingFlowOpen, setBookingFlowOpen] = React.useState(false);

	return (
		<article className='flex flex-col gap-6 text-neutral-800'>
			<button
				onClick={() => navigate('/')}
				className='self-start px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors'
			>
				← Back to Workshops
			</button>

			<div className='grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start'>
				{/* First column - Image */}
				<div className='md:col-span-3'>
					<ImageCard
						backgroundImage={
							workshop.image ||
							'https://via.placeholder.com/600x400?text=Workshop+Image'
						}
						className='image-card w-full'
					/>
				</div>

				{/* Second column - Workshop details */}
				<div className='md:col-span-6'>
					<div className='w-full'>
						{/* Workshop Header */}
						<div className='flex flex-col'>
							<h1 className='text-3xl font-bold text-neutral-800'>
								{workshop.title}
							</h1>
						</div>

						{/* Workshop Description */}
						{workshop.description && (
							<div className='mt-8'>
								<h2 className='text-xl font-semibold mb-4'>
									About this Workshop
								</h2>
								<p className='text-neutral-700'>
									{workshop.description as any}
								</p>

								<div className='mt-4'>
									<PortableText
										value={workshop.content}
										components={{
											block: {
												h1: ({ children }) => (
													<h1 className='text-3xl font-bold my-4'>
														{children}
													</h1>
												),
												h2: ({ children }) => (
													<h2 className='text-2xl font-bold my-3'>
														{children}
													</h2>
												),
												h3: ({ children }) => (
													<h3 className='text-xl font-bold my-2'>{children}</h3>
												),
												h4: ({ children }) => (
													<h4 className='text-lg font-semibold my-2'>
														{children}
													</h4>
												),
												normal: ({ children }) => (
													<p className='mb-4'>{children}</p>
												),
												blockquote: ({ children }) => (
													<blockquote className='pl-4 border-l-4 border-gray-300 italic my-4'>
														{children}
													</blockquote>
												),
											},
											list: {
												bullet: ({ children }) => (
													<ul className='list-disc pl-6 mb-4'>{children}</ul>
												),
												number: ({ children }) => (
													<ol className='list-decimal pl-6 mb-4'>{children}</ol>
												),
											},
											marks: {
												strong: ({ children }) => (
													<strong className='font-bold'>{children}</strong>
												),
												em: ({ children }) => (
													<em className='italic'>{children}</em>
												),
												link: ({ value, children }) => {
													const target = (value?.href || '').startsWith('http')
														? '_blank'
														: undefined;
													return (
														<a
															href={value?.href}
															target={target}
															rel={
																target === '_blank'
																	? 'noopener noreferrer'
																	: undefined
															}
															className='text-blue-600 hover:underline'
														>
															{children}
														</a>
													);
												},
											},
										}}
									/>
								</div>
							</div>
						)}

						{/* Workshop Benefits */}
						{workshop.benefits && workshop.benefits.length > 0 && (
							<div className='mt-4'>
								<h2 className='text-xl font-semibold mb-4'>
									What you'll learn
								</h2>
								<ul className='space-y-2'>
									{workshop.benefits.map((benefit, index) => (
										<li key={index} className='flex items-start'>
											<span className='text-emerald-500 mr-2'>✓</span>
											<span>{benefit}</span>
										</li>
									))}
								</ul>
							</div>
						)}
					</div>
				</div>

				{/* Third column - Reserve Button */}
				<div className='md:col-span-3 md:self-start md:sticky md:top-6'>
					<div className='bg-white p-6 rounded-lg shadow-sm border border-gray-100'>
						<h3 className='font-semibold text-lg mb-4'>Reserve your spot</h3>

						<div className='space-y-3 mb-6'>
							<div className='flex justify-between'>
								<span className='text-gray-600'>Date</span>
								<span className='font-medium'>{`${workshop.day} ${workshop.month} ${workshop.year}`}</span>
							</div>
							<div className='flex justify-between'>
								<span className='text-gray-600'>Time</span>
								<span className='font-medium'>{workshop.time}</span>
							</div>
							<div className='flex justify-between'>
								<span className='text-gray-600'>Location</span>
								<span className='font-medium'>{workshop.location}</span>
							</div>
							{!isSoldOut && (
								<div className='flex justify-between'>
									<span className='text-gray-600'>Available</span>
									<span className='font-medium'>{workshop.limit} seats</span>
								</div>
							)}
							<div className='flex justify-between text-lg'>
								<span className='font-medium'>Price</span>
								<span className='font-semibold'>R{workshop.price}</span>
							</div>
						</div>

						<button
							disabled={isSoldOut}
							onClick={() => setBookingFlowOpen(true)}
							className={`w-full py-4 rounded-lg text-center font-medium transition-colors ${
								isSoldOut
									? 'bg-gray-300 text-gray-500 cursor-not-allowed'
									: 'bg-[#fbb040] text-white hover:bg-[#f8c880]'
							}`}
						>
							{isSoldOut ? 'Sold Out' : 'Reserve Your Seat'}
						</button>
					</div>
				</div>
			</div>

			{/* Booking Flow */}
			<WorkshopBookingFlow
				isOpen={bookingFlowOpen}
				onClose={() => setBookingFlowOpen(false)}
				workshop={workshop}
			/>
		</article>
	);
}
